import React from 'react';
import { useRouteMatch, Link, generatePath, Route, Switch, useHistory, useLocation, matchPath } from 'react-router-dom';
import { useParams } from 'react-router';
import { Box, Typography, getContrastRatio } from '@mui/material';
import GroupAddUsersView from './GroupAddUsersView';
import routes from '../routes';
import { Roles } from '../models/roles';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import { Colors } from '../assets/theme/Colors';
import UsersListView from './UsersListView';
import DonorsLocationsListView from './DonorsLocationsListView';
import ReceiversListView from './ReceiversListView';
import RescuersListView from './RescuersListView';
import UsersUnassignedListView from './UsersUnassignedListView';
import InProgressRegistrationsListView from './InProgressRegistrationsListView';
import GroupsListView from './GroupsListView';
import UmbrellaFoodDonorsListView from './UmbrellaFoodDonorsListView';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';
import { FF_USER_GROUPS } from '../components/FeatureFlagWrapper';
import FoodDonorsUnassignedListView from './FoodDonorsUnassignedListView';
import NewLabel from '../components/NewLabel';

const FOOD_DONORS_TAB = 'food-donors';

const linkSx = {
  textDecoration: 'none',
  color: Colors.gray,
  paddingLeft: '10px',
  '&.active': {
    color: 'primary.main',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -3,
      top: 0,
      height: '100%',
      borderLeft: '4px solid',
      borderColor: 'primary.main',
    },
  },
};
const subLinkSx = (theme) => ({
  textDecoration: 'none',
  color: Colors.gray,
  marginLeft: '10px',
  padding: '3px 8px',
  position: 'relative',
  '&.active': {
    color: 'primary.dark',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: 5,
      backgroundColor: 'primary.main',
      filter:
        getContrastRatio(theme.palette.primary.main, '#fff') > 4.5
          ? 'brightness(4) saturate(0.1)'
          : 'brightness(1.5) saturate(0.7)',
      zIndex: -1,
    },
  },
});
const subMenuSx = {
  overflow: 'hidden',
  maxHeight: 0,
  opacity: 0,
  transition: 'all 0.5s ease-in-out',
  '&.show': {
    maxHeight: '200px',
    opacity: 1,
    transition: 'max-height 1s ease-in-out, opacity 0.7s ease-in-out',
  },
};

const usersList = [
  {
    name: 'People',
    component: UsersListView,
    path: generatePath(routes.allUsers, { tab: 'users' }),
    roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
    tab: 'rescues',
    locationState: true,
  },
  {
    name: 'Food Donors',
    path: generatePath(routes.allUsers, { tab: FOOD_DONORS_TAB }),
    roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
    tab: FOOD_DONORS_TAB,
    subList: [
      {
        name: 'My Donors',
        component: DonorsLocationsListView,
        path: generatePath(routes.allUsers, { tab: FOOD_DONORS_TAB, subTab: 'my-donors' }),
        roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
        subTab: 'my-donors',
      },
      {
        name: 'Umbrella Donors',
        component: UmbrellaFoodDonorsListView,
        path: generatePath(routes.allUsers, { tab: FOOD_DONORS_TAB, subTab: 'umbrella-food-donors' }),
        roles: [Roles.Admin, Roles.NationalSiteDirector],
        subTab: 'umbrella-food-donors',
      },
      {
        name: 'Unassigned Food Donors',
        component: FoodDonorsUnassignedListView,
        path: generatePath(routes.allUsers, { tab: FOOD_DONORS_TAB, subTab: 'unassigned-food-donors' }),
        roles: [Roles.Admin, Roles.NationalSiteDirector],
        subTab: 'unassigned-food-donors',
      },
    ],
  },
  {
    name: 'Receiving Agencies',
    component: ReceiversListView,
    path: generatePath(routes.allUsers, { tab: 'receivers' }),
    roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
    tab: 'receivers',
  },
  {
    name: 'Rescuers',
    component: RescuersListView,
    path: generatePath(routes.allUsers, { tab: 'rescuers' }),
    roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
    tab: 'rescuers',
  },
  {
    name: 'Unassigned',
    component: UsersUnassignedListView,
    path: generatePath(routes.allUsers, { tab: 'unassigned-users' }),
    roles: [Roles.Admin, Roles.NationalSiteDirector],
    tab: 'unassigned-users',
  },
  {
    name: 'In Progress Registrations',
    component: InProgressRegistrationsListView,
    path: generatePath(routes.allUsers, { tab: 'in-progress-registrations' }),
    roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteCoordinator, Roles.SiteDirector],
    tab: 'in-progress-registrations',
  },
  {
    name: 'Groups',
    isNew: true,
    component: GroupsListView,
    path: generatePath(routes.allUsers, { tab: 'groups' }),
    roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteCoordinator, Roles.SiteDirector],
    tab: 'groups',
    subTabAware: true,
  },
];

const AllUsersView = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
  const isGroupAddUsers = !!matchPath(location.pathname, { path: routes.groupAddUsers, exact: true });
  const hasUserGroupsFF = useHasActiveFeatureFlag(FF_USER_GROUPS);

  const userHasRoles = (roles = []) => currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite(roles)

  const userHasAcess = userHasRoles([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);

  const users = usersList.filter((user) => userHasRoles(user.roles)).filter((user) => !user.isNew || hasUserGroupsFF);

  const paths = users.map((user) => user.path);

  const getActiveTab = () => {
    if (isGroupAddUsers) {
      return Math.max(users.findIndex(u => u.tab === 'groups'), 0);
    }

    const path = paths.indexOf(url);

    if (path < 0) {
      return Math.max(
        users.findIndex((user) => user.tab === params.tab),
        0
      );
    }

    return Math.max(path, 0);
  };

  const getActiveSubTab = () => {
    const usersWithSubTabs = users.find((user) => user.subList?.length > 0 && params.tab === user.tab);

    if (usersWithSubTabs) {
      const subTabsPaths = usersWithSubTabs.subList.filter((user) => userHasRoles(user.roles)).map((sub) => sub.path);
      const path = subTabsPaths.indexOf(url);

      return Math.max(path, 0);
    }

    return null;
  };

  if (!userHasAcess) {
    history.push(routes.index);
  }

  return (
    <>
      <Typography variant="h5">Users</Typography>
      <Box mt={5} display="flex" gap="16px">
        <Box minWidth="max-content">
          {users.map((user, index) => (
            <Box key={user.name}>
              <Typography variant="subtitle2" gutterBottom>
                <Box component={Link} sx={linkSx} className={index !== getActiveTab() ? '' : 'active'} to={user.path}>
                  {user.name}
                </Box>
                {user.isNew && <NewLabel featureFlag={FF_USER_GROUPS} />}
              </Typography>
              {user.subList?.filter((u) => userHasRoles(u.roles)).length > 1 &&
                user.subList
                  .filter((u) => userHasRoles(u.roles))
                  .map((subUser, i) => (
                    <Box sx={subMenuSx} className={params.tab === user.tab ? 'show' : ''} key={subUser.name}>
                      <Typography variant="subtitle2" key={subUser.name} gutterBottom>
                        <Box component={Link} sx={subLinkSx} className={i !== getActiveSubTab() ? '' : 'active'} to={subUser.path}>
                          {subUser.name}
                        </Box>
                      </Typography>
                    </Box>
                  ))}
            </Box>
          ))}
        </Box>
        <Box sx={{ width: 'calc(100% - 170px)' }}>
          <Switch>
            <Route exact path={routes.groupAddUsers} component={GroupAddUsersView} />
            {users.map((user, index) => {
              if (user.subList?.length > 0) {
                return user.subList
                  .filter((user) => userHasRoles(user.roles))
                  .map((subUser, i) => (
                    <Route
                      exact
                      path={i === 0 ? [generatePath(routes.allUsers, { tab: user.tab }), subUser.path] : subUser.path}
                      component={subUser.component}
                      key={subUser.name}
                    />
                  ));
              }

              if (user.subTabAware) {
                return (
                  <Route
                    path={user.path}
                    render={(props) => React.createElement(user.component, { subTab: params.subTab, ...props })}
                    key={user.name}
                  />
                );
              }

              return (
                <Route
                  exact
                  path={index === 0 ? [generatePath(routes.allUsers), user.path] : user.path}
                  render={(props) => React.createElement(user.component, { locationState: location?.state, ...props })}
                  key={user.name}
                />
              );
            })}
          </Switch>
        </Box>
      </Box>
    </>
  );
};

export default AllUsersView;
